import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Container, Row, Col } from "reactstrap";
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';

import Main from '../../layout/main'
import ProPlusLoader from "../../components/proPlusPayment/proPlusSkeleton";
import AddCards from "../../containers/wallet-history/payment-form";
import { getProfileEditData } from '../../redux/actions/profileEditData';
import { getProfileData } from '../../redux/actions/profileData';
import axios from 'axios';
import API from '../../API';

const ProPlus = (props) => {
    const { ProfileData, ProfileDataLoading } = props;
    const history = useHistory();
    const dispatch = useDispatch();

    // State management
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [availablePlans, setAvailablePlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [availableCards, setAvailableCards] = useState([]);
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [showAddCard, setShowAddCard] = useState(false);
    const [upgradeLoading, setUpgradeLoading] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [resumeLoading, setResumeLoading] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showCancelSuccessAlert, setShowCancelSuccessAlert] = useState(false);
    const [showResumeSuccessAlert, setShowResumeSuccessAlert] = useState(false);
    const [showCancelConfirmAlert, setShowCancelConfirmAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [currentPackageData, setCurrentPackageData] = useState(null);
    const [currentPlanIndex, setCurrentPlanIndex] = useState(0);

    // Get profile data from Redux
    const profileData = useSelector(state => state.profileData?.profile);
    console.log('profileData', profileData);
    // Extract current plan data from profile or current package API
    const currentPlan = currentPackageData?.package || {};
    const currentSubscription = currentPackageData?.subscription || {};

    // Normalization function
    const normalizeBillingCycle = (cycle) => (cycle === 'yearly' ? 'annual' : cycle);

    // Determine current billing cycle from subscription
    const currentBillingCycle = currentSubscription.plan ? normalizeBillingCycle(currentSubscription.plan) : null;
    const isBillingCycleChanged = currentBillingCycle && billingCycle !== currentBillingCycle;

    // Format dates from API response
    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };
    console.log('currentPlan', currentPlan);
    const currentPlanData = {
        name: currentPlan.title || 'No Subscription',
        status: currentSubscription.status || 'Active',
        startDate: formatDate(currentSubscription.start_date),
        nextBillingDate: formatDate(currentSubscription.next_cycle_start),
        cancelledDate: currentSubscription?.status === 'cancelled' ? formatDate(currentSubscription.cancelled_at) : '',
        price: currentPlan.monthly_price || 0,
        features: currentPlan.features || [],
        endDate:  formatDate(currentSubscription.end_date) || ''
    };

    // Check if user is trying to upgrade or stay on current plan
    const isCurrentPlan = selectedPlan === currentPlan.id;
    const isPlanChanged = selectedPlan !== currentPlan.id;
    
    // Update: Enable confirm if user switches plan or changes billing cycle for current plan
    const isConfirmDisabled =
        ((selectedPlan === currentPlan.id && !isBillingCycleChanged)) || // On current plan and billing cycle not changed
        !selectedCardId || // No card selected
        !billingCycle; // No billing cycle selected

    // Fetch and set current package data (reusable function)
    const fetchAndSetCurrentPackage = async () => {
        try {
            const response = await axios.get(API.baseURL + '/v2/providers/current_package', API.options);
            if (response.status === 200 && response.data) {
                setCurrentPackageData(response.data);
                // Store subscription ID in localStorage for future use
                if (response.data.subscription && response.data.subscription.id) {
                    localStorage.setItem('subscription_id', response.data.subscription.id);
                }
            }
        } catch (error) {
            console.error('Failed to fetch current package:', error);
            // When API fails (e.g., subscription ended), we'll rely on stored subscription ID
        }
    };

    // Fetch current package data on component mount
    useEffect(() => {
        fetchAndSetCurrentPackage();
    }, []);

    // Fetch profile data on component mount
    useEffect(() => {
        if (!profileData || Object.keys(profileData).length === 0) {
            dispatch(getProfileData());
        }
    }, [dispatch, profileData]);

    // Fetch available cards on component mount
    useEffect(() => {
        fetchAvailableCards();
    }, []);

    // Fetch available plans
    useEffect(() => {
        const fetchAvailablePlans = async () => {
            try {
                const response = await axios.get(API.baseURL + '/v2/providers/pro_plans', API.options);
                if (response.status === 200 && response.data) {
                    setAvailablePlans(response.data);

                    // Set selected plan based on current plan
                    if (currentPlan.id) {
                        setSelectedPlan(currentPlan.id);
                    } else if (response.data.length > 0) {
                        setSelectedPlan(response.data[0].id);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch available plans:', error);
                setAvailablePlans([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAvailablePlans();
    }, []);

    // Update selected plan when current plan data becomes available
    useEffect(() => {
        if (currentPlan.id && availablePlans.length > 0) {
            // Check if current plan exists in available plans
            const planExists = availablePlans.find(p => p.id === currentPlan.id);
            if (planExists) {
                setSelectedPlan(currentPlan.id);
            } else if (availablePlans.length > 0) {
                // If current plan not found in available plans, select first available
                setSelectedPlan(availablePlans[0].id);
            }
        }
    }, [currentPlan.id, availablePlans]);

    // When setting billingCycle from API, normalize it
    useEffect(() => {
        if (currentSubscription && currentSubscription.plan) {
            setBillingCycle(normalizeBillingCycle(currentSubscription.plan));
        }
    }, [currentSubscription?.plan]);

    // Update billing cycle when selected plan changes to ensure it's valid for the new plan
    useEffect(() => {
        const selectedPlanData = availablePlans.find(p => p.id === selectedPlan);
        if (selectedPlanData) {
            const showMonthly = selectedPlanData.allow_monthly;
            const showYearly = selectedPlanData.allow_yearly;
            
            // If current billing cycle is not available for the selected plan, set to first available option
            if (billingCycle === 'monthly' && !showMonthly) {
                setBillingCycle(showYearly ? 'annual' : 'monthly');
            } else if (billingCycle === 'annual' && !showYearly) {
                setBillingCycle(showMonthly ? 'monthly' : 'annual');
            }
        }
    }, [selectedPlan, availablePlans, billingCycle]);

    const handleConfirm = async () => {
        if (isConfirmDisabled) return;

        setUpgradeLoading(true);

        try {
            const data = {
                package_id: selectedPlan,
                user_id: Cookies.get('provider_id'),
                payment_method: selectedCardId,
                plan: billingCycle // monthly or yearly
            };

            console.log('Upgrading package with data:', data);

            const response = await axios.post(API.baseURL + `/v2/providers/upgrade_package`, data, API.options);

            if (response.status === 200) {
                setShowSuccessAlert(true);
                // Refresh profile data to get updated package info
                dispatch(getProfileData());
            } else {
                setErrorMessage('Failed to upgrade package. Please try again.');
                setShowErrorAlert(true);
            }
        } catch (error) {
            console.error('Package upgrade error:', error);
            const errorMsg = error.response?.data?.error ||
                error.response?.data?.message ||
                'Failed to upgrade package. Please try again.';
            setErrorMessage(errorMsg);
            setShowErrorAlert(true);
        } finally {
            setUpgradeLoading(false);
        }
    };

    const handleCancel = () => {
        history.goBack();
    };

    const handleResumeSubscription = async () => {
        setResumeLoading(true);

        try {
            // Get subscription ID from current package data, profile data, or localStorage
            const subscriptionId = currentPackageData?.subscription?.id ||
                profileData?.subscription_id ||
                currentPlan.subscription_id ||
                localStorage.getItem('subscription_id');

            const data = {
                subscription_id: subscriptionId
            };

            console.log('Resuming subscription with data:', data);

            if (!subscriptionId) {
                setErrorMessage('Subscription ID not found. Please try again.');
                setShowErrorAlert(true);
                return;
            }

            const response = await axios.post(API.baseURL + `/v2/providers/resume_subscription`, data, API.options);

            if (response.status === 200) {
                setResumeLoading(false);
                setShowResumeSuccessAlert(true);
                // Refresh profile data to get updated package status
                dispatch(getProfileData());
                // Refresh current package data
                await fetchAndSetCurrentPackage();
            } else {
                setErrorMessage('Failed to resume subscription. Please try again.');
                setShowErrorAlert(true);
            }
        } catch (error) {
            console.error('Resume subscription error:', error);
            const errorMsg = error.response?.data?.error ||
                error.response?.data?.message ||
                'Failed to resume subscription. Please try again.';
            setErrorMessage(errorMsg);
            setShowErrorAlert(true);
        } finally {
            setResumeLoading(false);
        }
    };

    const handleCancelSubscription = () => {
        setShowCancelConfirmAlert(true);
    };

    const confirmCancelSubscription = async () => {
        setShowCancelConfirmAlert(false);
        setCancelLoading(true);

        try {
            // Get subscription ID from current package data, profile data, or localStorage
            const subscriptionId = currentPackageData?.subscription?.id ||
                profileData?.subscription_id ||
                currentPlan.subscription_id ||
                localStorage.getItem('subscription_id');

            const data = {
                package_id: currentPlan.id,
                subscription_id: subscriptionId,
                user_id: Cookies.get('provider_id')
            };

            console.log('Cancelling subscription with data:', data);

            if (!subscriptionId) {
                setErrorMessage('Subscription ID not found. Please try again.');
                setShowErrorAlert(true);
                return;
            }

            const response = await axios.post(API.baseURL + `/v2/providers/cancel_subscription`, data, API.options);

            if (response.status === 200) {
                setCancelLoading(false);
                setShowCancelSuccessAlert(true);
                
                // Update the profile data locally with cancelled status and current date
                const currentDate = new Date().toISOString();
                if (profileData) {
                    // Update the profile data to reflect cancellation
                    const updatedProfileData = {
                        ...profileData,
                        package_status: 'cancelled',
                        package: {
                            ...profileData.package,
                            updated_at: currentDate
                        }
                    };
                    // Dispatch updated profile data
                    dispatch({ type: 'SET_PROFILE_DATA', payload: updatedProfileData });
                }
                
                // Refresh profile data to get updated package status
                dispatch(getProfileData());
                // Always refetch current package data after cancellation (even if refresh fails)
               await fetchAndSetCurrentPackage();
            } else {
                setErrorMessage('Failed to cancel subscription. Please try again.');
                setShowErrorAlert(true);
            }
        } catch (error) {
            console.error('Cancel subscription error:', error);
            const errorMsg = error.response?.data?.error ||
                error.response?.data?.message ||
                'Failed to cancel subscription. Please try again.';
            setErrorMessage(errorMsg);
            setShowErrorAlert(true);
        } finally {
            setCancelLoading(false);
        }
    };

    // Card management functions
    const fetchAvailableCards = async (selectLatest = false) => {
        try {
            const response = await axios.get(API.baseURL + `/providers/wallet_get_provider_card_details`, API.options);
            if (response.data.code === 200) {
                const cards = response.data.data.stripe_card_details || [];
                setAvailableCards(cards);

                if (selectLatest && cards.length > 0) {
                    // Select the latest (last) card
                    setSelectedCardId(cards[cards.length - 1].id);
                } else if (cards.length > 0 && !selectedCardId) {
                    // Auto-select first card if available and no card is currently selected
                    setSelectedCardId(cards[0].id);
                }
            }
        } catch (err) {
            console.error("API Error:", err);
        }
    };

    const handleAddCardSuccess = async () => {
        await fetchAvailableCards(true); // Refresh cards list and select the latest
    };

    const handleSuccessAlertDismiss = () => {
        setShowSuccessAlert(false);
        history.goBack(); // Navigate back to previous screen
    };
    console.log('sada',currentPlanData?.endDate, 'sadsad',new Date().toISOString());
    return (
        <div>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Manage Subscription</title>
                <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css"></link>
            </Helmet>
            <Main additionalClass_main=" ebw-pro__main">
                <section className="ebw-pro-plus__section">
                    <Container className="ebw-pro-plus__container">
                        {ProfileDataLoading ? (
                            <ProPlusLoader />
                        ) : (
                            <div className="ebw-pro-plus__content">
                                {/* Current Plan Section */}
                                {(currentPlanData?.name !== 'No Subscription') && (
                                    <div className="ebw-pro-plus__current-plan">
                                        <div className="ebw-pro-plus__current-plan-header">
                                            <h3 className="ebw-pro-plus__section-title">Your Current Plan</h3>
                                            <div className="ebw-pro-plus__header-actions">
                                                <span className={`ebw-pro-plus__status-badge ${profileData?.package_status || currentPlanData.status}`}>
                                                    {(profileData?.package_status || currentPlanData.status).charAt(0).toUpperCase() + (profileData?.package_status || currentPlanData.status).slice(1).toLowerCase()}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="ebw-pro-plus__current-plan-content">
                                            <div className="ebw-pro-plus__current-plan-info">
                                                <h4 className="ebw-pro-plus__current-plan-name">{currentPlanData.name}</h4>
                                                <div className="ebw-pro-plus__current-plan-details">
                                                    <div className="ebw-pro-plus__detail-item">
                                                        <i className="fa fa-calendar"></i>
                                                        <span>Started on: {currentPlanData.startDate}</span>
                                                    </div>
                                                    <div className="ebw-pro-plus__detail-item">
                                                        <i className="fa fa-clock-o"></i>
                                                        <span>
                                                            {profileData?.package_status === 'cancelled' ?
                                                                `Cancelled on: ${currentPlanData.cancelledDate}` :
                                                                `Next Billing: ${currentPlanData.nextBillingDate}`
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                                {profileData?.package_status === 'cancelled' && (
                                                    <div className="ebw-pro-plus__expiration-notice">
                                                        <p>Access to paid features expires on <strong>{currentPlanData.endDate}</strong>, unless your subscription is resumed.</p>
                                                    </div>
                                                )}
                                                {profileData?.package_status === 'active' ? (
                                                    <button 
                                                        className="ebw-pro-plus__cancel-btn"
                                                        onClick={handleCancelSubscription}
                                                        disabled={cancelLoading}
                                                    >
                                                        {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
                                                    </button>
                                                ) : profileData?.package_status === 'cancelled' && (
                                                    <button 
                                                        className="ebw-pro-plus__resume-btn"
                                                        onClick={handleResumeSubscription}
                                                        disabled={resumeLoading}
                                                    >
                                                        {resumeLoading ? 'Resuming...' : 'Resume Subscription'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Upgrade Plan Section */}
                                <div className="ebw-pro-plus__upgrade-section">
                                    <h3 className="ebw-pro-plus__section-title">Upgrade Your Plan</h3>
                                    <p className="ebw-pro-plus__section-subtitle">
                                        Explore available plans and select one that works best for you
                                    </p>
                                    
                                    {loading ? (
                                        <div className="ebw-pro-plus__loading">
                                            <div>Loading plans...</div>
                                        </div>
                                    ) : availablePlans.length > 0 ? (
                                        <div className="ebw-pro-plus__plans-grid">
                                            {availablePlans.map((plan) => {
                                                const isCurrentPlan = currentPlan.id === plan.id;
                                                const isPro = plan.title?.toLowerCase().includes('pro');

                                                return (
                                                    <div 
                                                        key={plan.id}
                                                        className={`ebw-pro-plus__plan-card ${selectedPlan === plan.id ? 'ebw-pro-plus__plan-card--selected' : isCurrentPlan ? 'ebw-pro-plus__plan-card--current' : ''}`}
                                                        onClick={() => setSelectedPlan(plan.id)}
                                                    >
                                                        <div className="ebw-pro-plus__plan-header">
                                                            {plan?.allow_yearly && (
                                                                <div className="ebw-pro-plus__plan-crown">
                                                                    <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <g clipPath="url(#clip0_992_535)">
                                                                            <path d="M2.10001 4.40037L5.25001 6.50037L8.38951 2.10537C8.45888 2.00814 8.55048 1.9289 8.65667 1.87422C8.76286 1.81954 8.88057 1.79102 9.00001 1.79102C9.11944 1.79102 9.23716 1.81954 9.34335 1.87422C9.44953 1.9289 9.54113 2.00814 9.61051 2.10537L12.75 6.50037L15.9 4.40037C16.0192 4.32105 16.1587 4.27752 16.3018 4.27492C16.445 4.27232 16.5859 4.31075 16.7079 4.38568C16.8299 4.46061 16.928 4.56891 16.9904 4.69777C17.0528 4.82663 17.0771 4.97067 17.0603 5.11287L15.828 15.5881C15.8065 15.7706 15.7188 15.9388 15.5815 16.0608C15.4443 16.1829 15.267 16.2503 15.0833 16.2504H2.91676C2.73306 16.2503 2.55575 16.1829 2.41848 16.0608C2.2812 15.9388 2.1935 15.7706 2.17201 15.5881L0.939756 5.11212C0.923106 4.96998 0.947469 4.82604 1.00997 4.6973C1.07247 4.56856 1.1705 4.46039 1.29249 4.38556C1.41448 4.31073 1.55532 4.27236 1.69841 4.27498C1.8415 4.27761 1.98084 4.32111 2.10001 4.40037ZM9.00001 11.7504C9.39783 11.7504 9.77936 11.5923 10.0607 11.311C10.342 11.0297 10.5 10.6482 10.5 10.2504C10.5 9.85254 10.342 9.47101 10.0607 9.18971C9.77936 8.9084 9.39783 8.75037 9.00001 8.75037C8.60218 8.75037 8.22065 8.9084 7.93935 9.18971C7.65804 9.47101 7.50001 9.85254 7.50001 10.2504C7.50001 10.6482 7.65804 11.0297 7.93935 11.311C8.22065 11.5923 8.60218 11.7504 9.00001 11.7504Z" fill="currentColor"/>
                                                                        </g>
                                                                        <defs>
                                                                            <clipPath id="clip0_992_535">
                                                                                <rect width="18" height="18" fill="white" transform="translate(0 0.5)"/>
                                                                            </clipPath>
                                                                        </defs>
                                                                    </svg>
                                                                </div>
                                                            )}
                                                            <h4 className="ebw-pro-plus__plan-name">{plan.title}</h4>
                                                        </div>
                                                        <div className="ebw-pro-plus__plan-price">${plan.monthly_price?plan.monthly_price:plan.yearly_price}/{plan.allow_monthly?'month':'year'}</div>
                                                        <div className="ebw-pro-plus__plan-divider"></div>
                                                        <ul className="ebw-pro-plus__plan-features">
                                                            {plan.features && plan.features.length > 0 ? (
                                                                plan.features.map((feature, index) => (
                                                                    <li key={index}>{feature}</li>
                                                                ))
                                                            ) : (
                                                                <li>Pro features included</li>
                                                            )}
                                                        </ul>
                                                        <button 
                                                            className={`ebw-pro-plus__plan-btn ${isCurrentPlan ? 'ebw-pro-plus__plan-btn--current' : selectedPlan === plan.id ? 'ebw-pro-plus__plan-btn--selected' : 'ebw-pro-plus__plan-btn--default'}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedPlan(plan.id);
                                                            }}
                                                        >
                                                            {isCurrentPlan ? 'Current Plan' : 'Switch Plan'}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="ebw-pro-plus__no-plans">
                                            <div>No plans available</div>
                                            <div>Please try again later</div>
                                        </div>
                                    )}
                                </div>

                                {/* Billing Cycle and Payment Method Section */}
                                <div className="ebw-pro-plus__billing-payment-section">
                                    {/* Billing Cycle Card */}
                                    {(() => {
                                        const selectedPlanData = availablePlans.find(p => p.id === selectedPlan);
                                        const showMonthly = selectedPlanData?.allow_monthly;
                                        const showYearly = selectedPlanData?.allow_yearly;
                                        
                                        if (!showMonthly && !showYearly) {
                                            return null;
                                        }

                                        return (
                                            <div className="ebw-pro-plus__billing-card">
                                                {/* <h3 className="ebw-pro-plus__section-title">Billing Cycle</h3> */}
                                                {showMonthly && (
                                                    <div className="ebw-pro-plus__billing-option">
                                                        <div className="ebw-pro-plus__billing-option-content">
                                                            <div className="ebw-pro-plus__billing-option-left">
                                                                <input
                                                                    type="radio"
                                                                    id="monthly"
                                                                    name="billing"
                                                                    value="monthly"
                                                                    checked={normalizeBillingCycle(billingCycle) === 'monthly'}
                                                                    onChange={() => setBillingCycle('monthly')}
                                                                    style={{
                                                                        alignSelf: 'center',
                                                                        marginTop: '0',
                                                                        marginBottom: '0',
                                                                        width: '26px',
                                                                        height: '26px'
                                                                    }}
                                                                />
                                                                <div className="ebw-pro-plus__billing-label-price">
                                                                    <label htmlFor="monthly" style={{fontSize: '18px', fontWeight: '700'}}>Monthly</label>
                                                                    {selectedPlanData && (
                                                                        <div className="ebw-pro-plus__billing-price">
                                                                            <span className="ebw-pro-plus__billing-price--simple">
                                                                                <span className="price-amount">${selectedPlanData.monthly_price}</span>/month
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Separator - only show when both options are available */}
                                                {showMonthly && showYearly && (
                                                    <div className="ebw-pro-plus__billing-separator"></div>
                                                )}
                                                
                                                {showYearly && (
                                                    <div className="ebw-pro-plus__billing-option">
                                                        <div className="ebw-pro-plus__billing-option-content">
                                                            <div className="ebw-pro-plus__billing-option-left">
                                                                <input
                                                                    type="radio"
                                                                    id="annual"
                                                                    name="billing"
                                                                    value="annual"
                                                                    checked={normalizeBillingCycle(billingCycle) === 'annual'}
                                                                    onChange={() => setBillingCycle('annual')}
                                                                    style={{
                                                                        alignSelf: 'center',
                                                                        marginTop: '0',
                                                                        marginBottom: '0',
                                                                        width: '26px',
                                                                        height: '26px'
                                                                    }}
                                                                />
                                                                <div className="ebw-pro-plus__billing-label-price" style={{position:'relative'}}>
                                                                    <label htmlFor="annual">Yearly</label>
                                                                    {selectedPlanData && (
                                                                        <div className="ebw-pro-plus__billing-price">
                                                                            {(() => {
                                                                                const yearlyTotal = selectedPlanData.discounted_annual_price?selectedPlanData.annual_price:selectedPlanData.yearly_price;
                                                                                const discountPercentage = parseFloat(selectedPlanData.discounted_annual_price);
                                                                                return (
                                                                                    <>
                                                                                        {discountPercentage > 0 ? (
                                                                                            <div className="ebw-pro-plus__billing-price-row">
                                                                                                <span className="ebw-pro-plus__billing-price--old" >
                                                                                                    <span className="price-amount" style={{textDecoration: 'line-through'}}>${yearlyTotal}</span><span style={{textDecoration: 'line-through'}}>/year</span>
                                                                                                </span>
                                                                                                <span className="ebw-pro-plus__billing-price--new">
                                                                                                    <span className="price-amount">${Math.floor(selectedPlanData.discounted_annual_price)}</span>/year
                                                                                                </span>
                                                                                                <span className="ebw-pro-plus__billing-price--discount" style={{position:'absolute',right:0,top:0,color: '#3D84FB', backgroundColor:'#EFF6FF',borderRadius:'50px',padding:'4px 8px'}}>
                                                                                                    <span className="price-amount">Save ${Math.floor(selectedPlanData.total_discount_value)}</span>
                                                                                                </span>
                                                                                            </div>
                                                                                        ) : (
                                                                                            <span className="ebw-pro-plus__billing-price--simple">
                                                                                                <span className="price-amount">${yearlyTotal}</span>/year
                                                                                            </span>
                                                                                        )}
                                                                                    </>
                                                                                );
                                                                            })()}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {selectedPlanData && (() => {
                                                            const discountPercentage = parseFloat(selectedPlanData.annual_price_discount);
                                                            return discountPercentage > 0 ? (
                                                                <div className="ebw-pro-plus__save-badge">Save ${Math.round(discountPercentage)}</div>
                                                            ) : null;
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* Payment Method Card */}
                                    <div className="ebw-pro-plus__payment-card">
                                        {/* <h3 className="ebw-pro-plus__section-title">Payment Method</h3> */}
                                        {availableCards.length > 0 ? (
                                            <div>
                                                {availableCards.map((card) => (
                                                    <div
                                                        key={card.id}
                                                        className={`ebw-pro-plus__payment-method ${selectedCardId === card.id ? 'ebw-pro-plus__payment-method--selected' : ''}`}
                                                        onClick={() => setSelectedCardId(card.id)}
                                                    >
                                                        <div className="ebw-pro-plus__card-info">
                                                            <div className="ebw-pro-plus__card-number">{`•••• ${card.last4_digit}`}</div>
                                                            <div className="ebw-pro-plus__card-expiry">{`Expires ${card.exp_month < 10 ? '0' + card.exp_month : card.exp_month}/${String(card.exp_year).slice(-2)}`}</div>
                                                        </div>
                                                        {selectedCardId === card.id ? (
                                                            <div className="ebw-pro-plus__card-check">✓</div>
                                                        ) : (
                                                            <div className="ebw-pro-plus__card-arrow">
                                                                <i className="fa fa-chevron-right"></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                                <div
                                                    className="ebw-pro-plus__add-card"
                                                    onClick={() => setShowAddCard(true)}
                                                >
                                                    <i className="fa fa-plus"></i>
                                                    <span style={{ color: '#171717'}}>Add Card</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="ebw-pro-plus__no-cards">
                                                <p>No payment methods added.</p>
                                                <div
                                                    className="ebw-pro-plus__add-card"
                                                    onClick={() => setShowAddCard(true)}
                                                >
                                                    <i className="fa fa-plus"></i>
                                                    <span>Add a new card</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="ebw-pro-plus__actions">
                                    <button 
                                        className="ebw-pro-plus__confirm-btn"
                                        style={{borderRadius: '2px'}}
                                        onClick={handleConfirm}
                                        disabled={isConfirmDisabled || upgradeLoading}
                                    >
                                        {upgradeLoading ? 'UPGRADING...' : 'CONFIRM DETAILS'}
                                    </button>
                                    
                                    {isPlanChanged && !selectedCardId && (
                                        <div className="ebw-pro-plus__helper-text">
                                            Please select a payment method to upgrade
                                        </div>
                                    )}

                                    <button 
                                        className="ebw-pro-plus__cancel-action-btn"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </button>
                                </div>

                                {/* Add Card Modal */}
                                {showAddCard && (
                                    <AddCards
                                        showAddCard={showAddCard}
                                        setShowAddCard={setShowAddCard}
                                        onSuccess={handleAddCardSuccess}
                                    />
                                )}

                                {/* Success Alerts */}
                                {showSuccessAlert && (
                                    <div className="ebw-pro-plus__modal-overlay">
                                        <div className="ebw-pro-plus__modal ebw-pro-plus__modal--success">
                                            <div className="ebw-pro-plus__modal-header">
                                                <h3>Success!</h3>
                                                <button onClick={handleSuccessAlertDismiss} className="ebw-pro-plus__modal-close">
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            </div>
                                            <div className="ebw-pro-plus__modal-body">
                                                <p>Your package has been upgraded to {billingCycle === 'annual' ? 'Annual' : 'Monthly'} plan!</p>
                                            </div>
                                            <div className="ebw-pro-plus__modal-actions">
                                                <button onClick={handleSuccessAlertDismiss} className="ebw-pro-plus__btn-primary">
                                                    OK
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {showCancelSuccessAlert && (
                                    <div className="ebw-pro-plus__modal-overlay">
                                        <div className="ebw-pro-plus__modal ebw-pro-plus__modal--success">
                                            <div className="ebw-pro-plus__modal-header">
                                                <h3>Subscription Cancelled</h3>
                                                <button onClick={() => setShowCancelSuccessAlert(false)} className="ebw-pro-plus__modal-close">
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            </div>
                                            <div className="ebw-pro-plus__modal-body">
                                                <p>Your subscription has been successfully cancelled. You will continue to have access to premium features until the end of your current billing period.</p>
                                            </div>
                                            <div className="ebw-pro-plus__modal-actions">
                                                <button onClick={() => setShowCancelSuccessAlert(false)} className="ebw-pro-plus__btn-primary">
                                                    OK
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {showResumeSuccessAlert && (
                                    <div className="ebw-pro-plus__modal-overlay">
                                        <div className="ebw-pro-plus__modal ebw-pro-plus__modal--success">
                                            <div className="ebw-pro-plus__modal-header">
                                                <h3>Subscription Resumed</h3>
                                                <button onClick={() => setShowResumeSuccessAlert(false)} className="ebw-pro-plus__modal-close">
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            </div>
                                            <div className="ebw-pro-plus__modal-body">
                                                <p>Your subscription has been successfully resumed. You now have full access to all premium features.</p>
                                            </div>
                                            <div className="ebw-pro-plus__modal-actions">
                                                <button onClick={() => setShowResumeSuccessAlert(false)} className="ebw-pro-plus__btn-primary">
                                                    OK
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Cancellation Confirmation Modal */}
                                {showCancelConfirmAlert && (
                                    <div className="ebw-pro-plus__modal-overlay">
                                        <div className="ebw-pro-plus__modal">
                                        <button onClick={() => setShowCancelConfirmAlert(false)} className="ebw-pro-plus__modal-close" style={{backgroundColor: '#EDF1F2', position: 'absolute', right: '0px', top: '0px', zIndex: '1000'}}>
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            <div className="ebw-pro-plus__modal-header">
                                                <h3>Confirm Cancellation</h3>
                                               
                                            </div>
                                            <div className="ebw-pro-plus__modal-body">
                                                <p>This will remove access to all premium features.</p>
                                            </div>
                                            <div className="ebw-pro-plus__modal-actions">
                                                <button 
                                                    className="ebw-pro-plus__btn-primary"
                                                    onClick={confirmCancelSubscription}
                                                    disabled={cancelLoading}
                                                >
                                                    {cancelLoading ? 'Cancelling...' : 'CONFIRM'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Error Alert */}
                                {showErrorAlert && (
                                    <div className="ebw-pro-plus__modal-overlay">
                                        <div className="ebw-pro-plus__modal ebw-pro-plus__modal--error">
                                            <div className="ebw-pro-plus__modal-header">
                                                <h3>Error</h3>
                                                <button onClick={() => setShowErrorAlert(false)} className="ebw-pro-plus__modal-close">
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            </div>
                                            <div className="ebw-pro-plus__modal-body">
                                                <p>{errorMessage}</p>
                                            </div>
                                            <div className="ebw-pro-plus__modal-actions">
                                                <button onClick={() => setShowErrorAlert(false)} className="ebw-pro-plus__btn-primary">
                                                    OK
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Container>
                </section>
            </Main>
        </div>
    )
}

export default ProPlus;
